/** Sotto questa confidenza il giorno va evidenziato come poco affidabile. */
export const FORECAST_LOW_CONFIDENCE_PCT = 75;

/** Classe meteo di un giorno previsionale: guida l'accuracy attesa del modello. */
export type ForecastWeatherClass = 'sunny' | 'cloudy' | 'transition';

/** Salto di copertura nuvolosa (punti) che qualifica una transizione rapida. */
const TRANSITION_CLOUD_DELTA_PP = 35;
/** Sopra questa copertura media il giorno è nuvoloso. */
const CLOUDY_MIN_CLOUD_PCT = 40;

/**
 * nMAE atteso del modello FV per classe meteo. I giorni di transizione sono i
 * più incerti: l'errore di timing sul fronte nuvoloso pesa sull'intera giornata.
 */
const CLASS_BASE_ERROR_PCT: Record<ForecastWeatherClass, number> = {
	sunny: 6,
	cloudy: 15,
	transition: 26,
};

export type ForecastSource = 'real-data' | 'config-estimate' | 'no-data';

/** Moltiplicatore dell'errore in base alla qualità della fonte energetica. */
const SOURCE_ERROR_FACTOR: Record<ForecastSource, number> = {
	'real-data': 1,
	'config-estimate': 1.9,
	'no-data': 0,
};

/** Crescita dell'errore per ogni giorno di orizzonte previsionale. */
const HORIZON_ERROR_GROWTH = 0.06;
/** Penalità applicata quando manca il dato meteo del giorno. */
const MISSING_WEATHER_ERROR_FACTOR = 1.25;

export interface ForecastTableInputDay {
	date?: string | null;
	productionKwh: number;
	consumptionKwh: number;
	cloudCoverPct?: number | null;
	irradianceKwhM2?: number | null;
}

export interface ForecastTableParams {
	days: ForecastTableInputDay[];
	source: ForecastSource;
	lowConfidenceThresholdPct?: number;
}

export interface ForecastTableDay {
	date: string | null;
	productionKwh: number;
	consumptionKwh: number;
	/** Produzione − consumo: positivo = surplus, negativo = deficit. */
	balanceKwh: number;
	/** Quota di consumo coperta dalla produzione prevista. */
	coveragePct: number | null;
	confidencePct: number;
	expectedErrorPct: number;
	weatherClass: ForecastWeatherClass;
	cloudCoverPct: number | null;
	irradianceKwhM2: number | null;
	lowConfidence: boolean;
}

export interface ForecastAccuracyClass {
	weatherClass: ForecastWeatherClass;
	days: number;
	avgConfidencePct: number;
	/** nMAE atteso medio della classe. */
	expectedErrorPct: number;
	lowConfidenceDays: number;
	productionKwh: number;
}

export interface ForecastTable {
	available: boolean;
	source: ForecastSource;
	lowConfidenceThresholdPct: number;
	days: ForecastTableDay[];
	accuracy: ForecastAccuracyClass[];
	lowConfidenceDays: number;
	avgConfidencePct: number;
	totalProductionKwh: number;
	totalConsumptionKwh: number;
}

const round1 = (value: number) => Math.round(value * 10) / 10;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const finiteOr = (value: number | null | undefined, fallback: number) =>
	typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const normalizeCloud = (value: number | null | undefined) =>
	value == null ? null : clamp(finiteOr(value, 0), 0, 100);

/**
 * Classifica un giorno da copertura nuvolosa media e salto rispetto al giorno
 * precedente. Il fronte in arrivo/uscita prevale sul valore assoluto: è la
 * condizione in cui la previsione FV sbaglia di più.
 */
export function classifyForecastDay(
	cloudCoverPct: number | null | undefined,
	previousCloudCoverPct?: number | null,
): ForecastWeatherClass {
	const cloud = normalizeCloud(cloudCoverPct);
	const previous = normalizeCloud(previousCloudCoverPct);
	if (cloud == null) return 'cloudy';
	if (previous != null && Math.abs(cloud - previous) >= TRANSITION_CLOUD_DELTA_PP) {
		return 'transition';
	}
	return cloud >= CLOUDY_MIN_CLOUD_PCT ? 'cloudy' : 'sunny';
}

/**
 * Costruisce la tabella previsionale giornaliera con produzione attesa,
 * confronto sul consumo stimato e percentuale di confidenza.
 *
 * La confidenza non è un valore arbitrario: deriva dall'nMAE atteso del
 * modello, che dipende dalla classe meteo del giorno, dalla qualità della
 * fonte energetica e dalla distanza dell'orizzonte previsionale.
 */
export function buildForecastTable(params: ForecastTableParams): ForecastTable {
	const source = params.source;
	const lowConfidenceThresholdPct = clamp(
		finiteOr(params.lowConfidenceThresholdPct, FORECAST_LOW_CONFIDENCE_PCT),
		0,
		100,
	);
	const sourceFactor = SOURCE_ERROR_FACTOR[source] ?? 0;

	const days = params.days.slice(0, 7).map<ForecastTableDay>((rawDay, index, all) => {
		const productionKwh = Math.max(0, finiteOr(rawDay.productionKwh, 0));
		const consumptionKwh = Math.max(0, finiteOr(rawDay.consumptionKwh, 0));
		const cloudCoverPct = normalizeCloud(rawDay.cloudCoverPct);
		const irradianceKwhM2 =
			rawDay.irradianceKwhM2 == null ? null : Math.max(0, finiteOr(rawDay.irradianceKwhM2, 0));
		const weatherClass = classifyForecastDay(cloudCoverPct, all[index - 1]?.cloudCoverPct);

		const expectedErrorPct =
			sourceFactor > 0
				? CLASS_BASE_ERROR_PCT[weatherClass] *
					sourceFactor *
					(1 + HORIZON_ERROR_GROWTH * index) *
					(cloudCoverPct == null ? MISSING_WEATHER_ERROR_FACTOR : 1)
				: 0;
		const confidencePct = sourceFactor > 0 ? clamp(Math.round(100 - expectedErrorPct), 1, 99) : 0;

		return {
			date: rawDay.date ?? null,
			productionKwh: round1(productionKwh),
			consumptionKwh: round1(consumptionKwh),
			balanceKwh: round1(productionKwh - consumptionKwh),
			coveragePct: consumptionKwh > 0 ? Math.round((productionKwh / consumptionKwh) * 100) : null,
			confidencePct,
			expectedErrorPct: sourceFactor > 0 ? round1(expectedErrorPct) : 0,
			weatherClass,
			cloudCoverPct,
			irradianceKwhM2: irradianceKwhM2 == null ? null : round1(irradianceKwhM2),
			lowConfidence: confidencePct > 0 && confidencePct < lowConfidenceThresholdPct,
		};
	});

	const classes: ForecastWeatherClass[] = ['sunny', 'cloudy', 'transition'];
	const accuracy = classes
		.map<ForecastAccuracyClass>((weatherClass) => {
			const classDays = days.filter((day) => day.weatherClass === weatherClass);
			const total = (pick: (day: ForecastTableDay) => number) =>
				classDays.reduce((sum, day) => sum + pick(day), 0);
			return {
				weatherClass,
				days: classDays.length,
				avgConfidencePct: classDays.length
					? Math.round(total((day) => day.confidencePct) / classDays.length)
					: 0,
				expectedErrorPct: classDays.length
					? round1(total((day) => day.expectedErrorPct) / classDays.length)
					: 0,
				lowConfidenceDays: classDays.filter((day) => day.lowConfidence).length,
				productionKwh: round1(total((day) => day.productionKwh)),
			};
		})
		.filter((entry) => entry.days > 0);

	const rated = days.filter((day) => day.confidencePct > 0);

	return {
		available: days.length > 0 && source !== 'no-data',
		source,
		lowConfidenceThresholdPct,
		days,
		accuracy,
		lowConfidenceDays: days.filter((day) => day.lowConfidence).length,
		avgConfidencePct: rated.length
			? Math.round(rated.reduce((sum, day) => sum + day.confidencePct, 0) / rated.length)
			: 0,
		totalProductionKwh: round1(days.reduce((sum, day) => sum + day.productionKwh, 0)),
		totalConsumptionKwh: round1(days.reduce((sum, day) => sum + day.consumptionKwh, 0)),
	};
}
